/* global graphql */

import React from "react";

import * as R from "ramda";

import { compose, withState, withProps, withHandlers } from "recompose";

import { Box, Flex, ItemGroup, Link } from "src/components";
import { shortenLocaleName } from "src/utils";

import { colors } from "src/styles";

import { Arrow } from "src/icons";
import { defaultPrevented } from "src/utils";

const MenuLink = ({ page, ...props }) => (
  <Link
    to={page.url}
    color="muted"
    textTransform="uppercase"
    fontWeight="semibold"
    fontSize={3}
    exact={true}
    textDecoration="none"
    align={["center", "center", "center", "left"]}
    hover={{
      textDecoration: "underline",
      color: colors.brandGreen,
    }}
    activeStyle={{
      color: colors.brandGreen,
    }}
    {...props}
  >
    {page.title}
  </Link>
);

const SecondLevelLink = ({ page }) => <MenuLink page={page} mt={1} ml={2} />;

const enhanceTopLevelLink = compose(
  withState("expanded", "setExpanded", false),
  withProps(props => ({
    canExpand: props.showChildren && props.page.childPages,
  })),
  withHandlers({
    handleToggleMenu: props =>
      defaultPrevented(() => {
        props.setExpanded(!props.expanded);
      }),
  })
);

const TopLevelLink = enhanceTopLevelLink(
  ({
    page,
    canExpand,
    expanded,
    showChildren,
    handleToggleMenu,
    arrowSpacer = 48,
  }) => (
      <Flex my={2} flexDirection="column">
        <Flex align="center" justify={["center", "center", "center", "stretch"]}>
          <MenuLink
            page={page}
            fontWeight="bold"
            fontSize={[3, 3, 5, 6]}
            ml={arrowSpacer}
            mr={canExpand ? 0 : arrowSpacer}
          />
          {canExpand && (
            <Link
              to="#"
              title={expanded ? "Hide menu" : "Show menu"}
              onClick={handleToggleMenu}
              p={2}
            >
              <Arrow
                direction={expanded ? "up" : "down"}
                size={16}
                color="muted"
              />
            </Link>
          )}
        </Flex>
        {showChildren &&
          expanded &&
          page.childPages &&
          page.childPages.map(page => (
            <SecondLevelLink key={page.id} page={page} />
          ))}
      </Flex>
    )
);

const TableOfContents = ({ root }) => (
  <Box flex={[null, null, null, 2]} mx={[1, 1, 4]}>
    <TopLevelLink page={root} />
    {root.childPages.map(page => (
      <TopLevelLink key={page.id} page={page} showChildren={true} />
    ))}
  </Box>
);

const findLocaleNode = locale =>
  R.pipe(
    R.prop("edges"),
    R.map(R.prop("node")),
    R.find(
      R.pipe(
        R.prop("node_locale"),
        shortenLocaleName,
        R.equals(locale)
      )
    )
  );

const Menu = ({ menu, menuLinks, menuVisible, t, locale }) => {
  const findNode = findLocaleNode(locale);

  const root = findNode(menu);

  const menuLinksForLocale = findNode(menuLinks);

  return (
    <Flex bg="white" px={[1, 1, 1, 5, 6]}>
      {menuVisible ? (
        <Flex
          flex={1}
          justify="center"
          my={3}
          align={["center", "center", "center", "stretch"]}
          flexDirection={["column", "column", "column", "row"]}
        >
          <TableOfContents root={root} />
          <Flex
            flex={[undefined, undefined, undefined, 1]}
            flexDirection="column"
            mt={[2, 2, 2, 0]}
          >
            <ItemGroup
              {...menuLinksForLocale}
              theme="muted"
              padded={false}
              itemProps={{
                textTransform: "uppercase",
                fontWeight: "semibold",
                align: ["center", "center", "center", "flex-start"],
                fontSize: 3,
                mb: 2,
                activeStyle: {}, // Remove bold when selected
              }}
            />
          </Flex>
        </Flex>
      ) : null}
    </Flex>
  );
};

export default Menu;

export const MenuQuery = graphql`
  fragment MenuQuery on RootQueryType {
    menuLinks: allContentfulItemGroup(
      filter: { key: { eq: "menu-quicklinks" } }
    ) {
      edges {
        node {
          ...ItemGroupContent
          node_locale
        }
      }
    }
    menu: allContentfulPage(filter: { slug: { eq: "[home]" } }) {
      edges {
        node {
          node_locale
          ...PageMenuContent
          childPages {
            ...PageMenuContent
            childPages {
              ...PageMenuContent
              childPages {
                ...PageMenuContent
                childPages {
                  ...PageMenuContent
                }
              }
            }
          }
        }
      }
    }
  }

  fragment PageMenuContent on ContentfulPage {
    id
    slug
    title
    url
  }
`;



// WEBPACK FOOTER //
// ./src/components/Layout/Menu.js